import router from './router'
import { ElMessage } from 'element-plus'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { getToken } from '@/utils/auth'
import { isHttp } from '@/utils/validate'
import { isRelogin } from '@/utils/request'
import useUserStore from '@/store/modules/user'
import useSettingsStore from '@/store/modules/settings'
import usePermissionStore from '@/store/modules/permission'
import { forceLightThemeTemporarily, restorePersistedThemeMode } from '@/utils/theme'

NProgress.configure({ showSpinner: false });

const whiteList = ['/login', '/register', '/public/dashboard'];
// 带动态参数的免登录页面前缀（如作品上映排期页 /public/schedule/:pageKey）
const whiteListPrefixes = ['/public/schedule/'];

// 是否为免登录页面：精确匹配 whiteList，或匹配前缀
function isWhiteListed(path) {
  return whiteList.includes(path) || whiteListPrefixes.some((prefix) => path.startsWith(prefix));
}

// 标记是否是首次加载
let isInitialLoad = true;

router.beforeEach((to, from, next) => {
  // 登录/注册强制 light（不持久化）；离开后按用户偏好恢复
  if (isWhiteListed(to.path)) {
    forceLightThemeTemporarily()
  } else if (isWhiteListed(from.path)) {
    restorePersistedThemeMode()
  }

  // 判断是否是首次加载：from.name 不存在或 from.path 为 '/' 或为空
  const isFirstLoad = isInitialLoad && (!from.name && (from.path === '/' || from.path === ''));
  
  // 如果不是首次加载，显示 NProgress
  if (!isFirstLoad) {
    NProgress.start();
  }
  
  // 首次加载完成后，设置标志为 false
  if (isInitialLoad) {
    isInitialLoad = false;
  }
  if (getToken()) {
    to.meta.title && useSettingsStore().setTitle(to.meta.title)
    /* has token*/
    if (to.path === '/login') {
      next({ path: '/' })
      if (!isFirstLoad) {
        NProgress.done()
      }
    } else if (isWhiteListed(to.path)) {
      // 免登录白名单页面：即使浏览器残留（可能已过期/失效的）token，也无需校验登录状态，直接放行
      // 否则会触发 getInfo() 拉取用户信息，token 失效时会弹出"重新登录"提示框
      next()
      if (!isFirstLoad) {
        NProgress.done()
      }
    } else {
      if (useUserStore().roles.length === 0) {
        isRelogin.show = true
        // 判断当前用户是否已拉取完user_info信息
        useUserStore().getInfo().then(() => {
          isRelogin.show = false
          usePermissionStore().generateRoutes().then(accessRoutes => {
            // 根据roles权限生成可访问的路由表
            accessRoutes.forEach(route => {
              if (!isHttp(route.path)) {
                router.addRoute(route) // 动态添加可访问路由表
              }
            })
            // /client/client-v2 は権限によっては存在しない動的ルート。
            // 解決結果が 404 キャッチオール（/:pathMatch(.*)*）のみならば /index へフォールバックする。
            if (to.path === '/client/client-v2') {
              const resolved = router.resolve(to.fullPath)
              const fallsToNotFound =
                resolved.matched.length > 0 &&
                resolved.matched.every((r) => r.path === '/:pathMatch(.*)*')
              if (fallsToNotFound) {
                next({ path: '/index', replace: true })
                return
              }
            }
            next({ ...to, replace: true }) // hack方法 确保addRoutes已完成
          })
        }).catch(err => {
          useUserStore().logOut().then(() => {
            ElMessage.error(err)
            next({ path: '/' })
          })
        })
      } else {
        next()
      }
    }
  } else {
    // 没有token
    if (isWhiteListed(to.path)) {
      // 在免登录白名单，直接进入
      next()
    } else {
      next(`/login?redirect=${to.fullPath}`) // 否则全部重定向到登录页
      if (!isFirstLoad) {
        NProgress.done()
      }
    }
  }
})

// 根据路由 meta.robots 动态设置/清除 <meta name="robots">，避免影响其他页面的搜索引擎收录
function setRobotsMeta(to) {
  let metaTag = document.querySelector('meta[name="robots"]')
  if (to.meta && to.meta.robots) {
    if (!metaTag) {
      metaTag = document.createElement('meta')
      metaTag.setAttribute('name', 'robots')
      document.head.appendChild(metaTag)
    }
    metaTag.setAttribute('content', to.meta.robots)
  } else if (metaTag) {
    metaTag.remove()
  }
}

router.afterEach((to) => {
  setRobotsMeta(to)
  // 只有在非首次加载时才调用 NProgress.done()
  // 首次加载时，loading-screen 会处理淡入效果
  if (!isInitialLoad) {
    NProgress.done()
  }
})
